import styled, { keyframes } from 'styled-components';
import { Button } from 'antd';

/* ============================================================
   Tokens — from Cascade_DESIGN.md (CascadeMD Design System v1.0)
   Krub is inherited from the global theme (no font-family set here,
   so every element uses the project typeface).
   ============================================================ */
export const C = {
  brandBlue: '#47A4EA',
  brandBlueDark: '#2B8CD4',
  brandBlueTint: '#EBF4FD',
  ink: '#1A2332',
  slate: '#525E6F',
  muted: '#6B7A8D',
  placeholder: '#A0AAB4',
  white: '#FFFFFF',
  surface: '#F7F8FA',
  pageBg: '#F0F2F5',
  border: '#E8ECF0',
  borderStrong: '#D9D9D9',
  disabled: '#F5F5F5',
  danger: '#FF4D4F',
  success: '#52C41A',
  warning: '#FA8C16',
  rowBorder: '#F0F2F5',
  rowHover: '#FAFBFC',
  radiusBtn: '6px',
  radiusCard: '10px',
  radiusPill: '11px'
};

/* Status badge tints (Section 09). Tinted bg + bordered, never solid. */
export const statusBadgeColor = (status = '') => {
  const k = String(status)
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  if (k.includes('complet'))
    return { bg: '#F0FFF4', fg: '#15803D', border: '#86EFAC' };
  if (k.includes('bill'))
    return { bg: '#FFF7ED', fg: '#C2410C', border: '#FCD34D' };
  if (k.includes('struck') || k.includes('struk') || k.includes('fail') || k.includes('reject'))
    return { bg: '#FFF0F0', fg: '#B91C1C', border: '#FCA5A5' };
  if (k.includes('review'))
    return { bg: '#EBF4FD', fg: '#1A7DC4', border: '#C3DEFA' };
  // In Progress / Available / neutral default
  return { bg: '#F7F8FA', fg: '#525E6F', border: '#E2E8F0' };
};

export const StatusBadge = styled.span`
  height: 22px;
  padding: 0 10px;
  border-radius: ${C.radiusPill};
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  background: ${props => statusBadgeColor(props.status).bg};
  color: ${props => statusBadgeColor(props.status).fg};
  border: 1px solid ${props => statusBadgeColor(props.status).border};
`;

/* ============================================================
   Split layout: compact list (left) + detail (right)
   ============================================================ */
export const SplitShell = styled.div`
  display: grid;
  grid-template-columns: var(--list-w, 600px) 6px minmax(0, 1fr);
  gap: 0;
  border: 1px solid ${C.border};
  /* square left corners (list side); keep the detail side rounded */
  border-radius: 0 ${C.radiusCard} ${C.radiusCard} 0;
  overflow: hidden;
  background: ${C.white};
  height: calc(100vh - 230px);
  min-height: 460px;
  &.resizing {
    cursor: col-resize;
    user-select: none;
  }
  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

/* Drag handle between the list and detail columns. */
export const Splitter = styled.div`
  cursor: col-resize;
  position: relative;
  background: transparent;
  touch-action: none;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 1px;
    background: ${C.border};
    transition: background 0.15s ease, width 0.15s ease;
  }
  &:hover::before,
  &.active::before {
    background: ${C.brandBlue};
    width: 2px;
  }
  @media (max-width: 980px) {
    display: none;
  }
`;

/* ----- compact list column ----- */
export const ListColumn = styled.div`
  border-right: 1px solid ${C.border};
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: ${C.white};
  @media (max-width: 980px) {
    border-right: none;
    border-bottom: 1px solid ${C.border};
    max-height: 320px;
  }
`;

export const ListHead = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${C.border};
`;

/* search + filter row — flex, space-between */
export const ListSearchBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

/* Form input spec (Section 06) with a leading search icon */
export const ListSearch = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  & .anticon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: ${C.placeholder};
    font-size: 14px;
  }
  & input {
    width: 100%;
    height: 36px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 400;
    color: ${C.ink};
    background: ${C.white};
    border: 1px solid ${C.border};
    border-radius: ${C.radiusBtn};
    padding: 0 12px 0 34px;
  }
  & input::placeholder {
    color: ${C.placeholder};
  }
  & input:hover {
    border-color: #c0cbd8;
  }
  & input:focus {
    outline: none;
    border: 1.5px solid ${C.brandBlue};
    box-shadow: 0 0 0 3px rgba(71, 164, 234, 0.15);
  }
`;

/* Icon Neutral button (Section 05) — 36×36 */
export const ListFilterButton = styled.button`
  flex: none;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  background: ${C.white};
  color: ${C.slate};
  border: 1px solid ${C.borderStrong};
  border-radius: ${C.radiusBtn};
  cursor: pointer;
  font-size: 15px;
  &:hover {
    background: ${C.surface};
  }
`;

export const BackButton = styled.button`
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  color: ${C.brandBlue};
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: ${C.radiusBtn};
  &:hover {
    background: ${C.surface};
  }
`;

export const ListMeta = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${C.muted};
`;

export const ListRows = styled.div`
  overflow: auto;
  flex: 1;
  min-height: 0;
`;

/* ----- compact list pagination footer ----- */
export const ListFoot = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid ${C.border};
  background: ${C.white};
`;

export const PageInfo = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${C.muted};
  white-space: nowrap;
`;

export const PageControls = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const PageBtn = styled.button`
  width: 28px;
  height: 28px;
  display: grid;
  place-items: center;
  background: ${C.white};
  color: ${C.slate};
  border: 1px solid ${C.borderStrong};
  border-radius: ${C.radiusBtn};
  cursor: pointer;
  font-size: 12px;
  &:hover:not(:disabled) {
    background: ${C.surface};
  }
  &:disabled {
    color: ${C.placeholder};
    background: ${C.disabled};
    cursor: not-allowed;
  }
`;

/* Sticky table header for the compact list. grid-template-columns is set
   inline to match the currently-visible column set (width-driven). */
export const ListHeaderRow = styled.div`
  display: grid;
  align-items: center;
  gap: 12px;
  padding: 8px 24px;
  border-left: 3px solid transparent;
  border-bottom: 1px solid ${C.border};
  background: ${C.surface};
  position: sticky;
  top: 0;
  z-index: 1;
  width: max-content;
  min-width: 100%;
`;

export const HeaderCell = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  position: relative;
  ${props => props.dragOver && `box-shadow: inset 2px 0 0 ${C.brandBlue};`}
  ${props => props.dragging && 'opacity: 0.5;'}
`;

/* Column resize grip at the right edge of a header cell. */
export const ResizeHandle = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  right: -12px;
  width: 16px;
  cursor: col-resize;
  z-index: 3;
  &::after {
    content: '';
    position: absolute;
    top: 3px;
    bottom: 3px;
    left: 7px;
    width: 2px;
    background: transparent;
    transition: background 0.15s ease;
  }
  &:hover::after {
    background: ${C.brandBlue};
  }
`;

export const HeaderLabel = styled.span`
  flex: 1;
  min-width: 0;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  text-align: center;
  color: ${C.slate};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover {
    color: ${C.ink};
  }
`;

/* Clickable sort control sitting to the LEFT of the column name. */
export const HeaderSortIcon = styled.span`
  flex: none;
  cursor: pointer;
  display: inline-flex;
  flex-direction: column;
  line-height: 0;
`;

/* Filter control sitting to the RIGHT of the column name. */
export const HeaderFilterIcon = styled.span`
  flex: none;
  cursor: pointer;
  font-size: 11px;
  display: inline-flex;
  color: ${props => (props.active ? C.brandBlue : C.placeholder)};
  &:hover {
    color: ${C.brandBlue};
  }
`;

export const FilterMenu = styled.div`
  width: 200px;
  & .list {
    max-height: 220px;
    overflow: auto;
    padding-left: 8px;
  }
  & .row {
    padding: 3px 0;
  }
  & .actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 8px;
    padding-top: 8px;
    padding-right: 8px;
    padding-bottom: 8px;
    border-top: 1px solid ${C.border};
  }
`;

export const ListRow = styled.div`
  display: grid;
  align-items: center;
  gap: 12px;
  padding: 12px 24px;
  border-bottom: 1px solid ${C.rowBorder};
  cursor: pointer;
  border-left: 3px solid
    ${props => (props.selected ? C.brandBlue : 'transparent')};
  background: ${props => (props.selected ? C.brandBlueTint : 'transparent')};
  width: max-content;
  min-width: 100%;
  &:hover {
    background: ${props => (props.selected ? C.brandBlueTint : C.rowHover)};
  }
`;

export const Cell = styled.div`
  min-width: 0;
  font-size: 13px;
  font-weight: ${props => (props.strong ? 600 : 400)};
  color: ${props => (props.strong ? C.ink : C.slate)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
`;

/* ============================================================
   Detail panel
   ============================================================ */
export const DetailColumn = styled.div`
  overflow-y: auto;
  min-height: 0;
  padding: 24px 24px 40px;
  background: ${C.pageBg};
`;

export const DetailBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

export const DetailTitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const DetailTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: ${C.ink};
`;

export const DetailSub = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: ${C.muted};
  margin-top: 4px;
`;

export const Acts = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

/* Neutral utility button (Section 05) */
export const ActButton = styled(Button)`
  &&& {
    height: 36px;
    font-size: 14px;
    font-weight: 400;
    border: 1px solid ${C.borderStrong};
    background: ${C.white};
    color: ${C.ink};
    border-radius: ${C.radiusBtn};
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0 16px;
  }
  &&&:hover {
    background: ${C.surface};
    border-color: ${C.borderStrong};
    color: ${C.ink};
  }
  &&& .anticon {
    color: ${C.slate};
  }
`;

/* ----- header card / fact grid ----- */
export const HeaderCard = styled.div`
  background: ${C.white};
  border: 1px solid ${C.border};
  border-radius: ${C.radiusCard};
  padding: 20px 24px;
  margin-bottom: 16px;
`;

export const FactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px 24px;
  @media (max-width: 760px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const FactKey = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${C.slate};
  margin-bottom: 4px;
`;

export const FactVal = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${C.ink};
  word-break: break-word;
`;

export const BodyGrid = styled.div`
  display: block;
`;

/* ----- tabs (Section 07) ----- */
export const Tabs = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid ${C.border};
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const Tab = styled.button`
  font-family: inherit;
  font-size: 14px;
  font-weight: ${props => (props.active ? 500 : 400)};
  color: ${props => (props.active ? C.brandBlue : C.muted)};
  background: none;
  border: none;
  padding: 12px;
  cursor: pointer;
  border-bottom: 2px solid
    ${props => (props.active ? C.brandBlue : 'transparent')};
  margin-bottom: -1px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  &:hover {
    color: ${C.brandBlue};
  }
  & .pill {
    font-size: 12px;
    font-weight: 500;
    background: ${props => (props.active ? C.brandBlue : C.surface)};
    color: ${props => (props.active ? C.white : C.slate)};
    padding: 0 8px;
    border-radius: 10px;
  }
`;

export const Panel = styled.div`
  background: ${C.white};
  border: 1px solid ${C.border};
  border-radius: ${C.radiusCard};
  padding: 20px 24px;
  & h3 {
    margin: 0 0 4px;
    font-size: 16px;
    font-weight: 600;
    color: ${C.ink};
  }
  & .sub {
    font-size: 13px;
    font-weight: 400;
    color: ${C.muted};
    margin: 0 0 16px;
  }
`;

export const EmptyState = styled.div`
  padding: 40px 24px;
  text-align: center;
  color: ${C.muted};
  font-size: 14px;
`;

/* ============================================================
   Timeline
   ============================================================ */
export const Trail = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  overflow-x: auto;
  padding: 8px 0 16px;
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${C.surface};
  }
  &::-webkit-scrollbar-thumb {
    background: ${C.border};
    border-radius: 3px;
  }
`;

export const TimelineItem = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 160px;
  flex: 1 0 160px;
  padding: 36px 12px 0;
  /* Horizontal connector line through dot center */
  &::before {
    content: '';
    position: absolute;
    top: 10px;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props =>
      props.kind === 'ai'
        ? C.brandBlue
        : props.kind === 'done'
        ? C.success
        : C.border};
    z-index: 0;
  }
  &:first-child::before {
    left: 50%;
  }
  &:last-child::before {
    right: 50%;
  }
`;

export const TimelineDot = styled.span`
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${C.white};
  border: 2px solid
    ${props =>
      props.kind === 'ai'
        ? C.brandBlue
        : props.kind === 'done'
        ? C.success
        : props.kind === 'sys'
        ? C.slate
        : C.border};
  color: ${props =>
    props.kind === 'ai'
      ? C.brandBlue
      : props.kind === 'done'
      ? C.success
      : props.kind === 'sys'
      ? C.slate
      : C.placeholder};
  font-size: 12px;
  z-index: 1;
`;

export const TimelineHead = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
`;

export const TimelineAct = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${C.ink};
  text-align: center;
`;

export const TimelineWho = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${C.slate};
  background: ${C.surface};
  border: 1px solid ${C.border};
  padding: 2px 8px;
  border-radius: ${C.radiusPill};
  text-transform: capitalize;
`;

export const TimelineTime = styled.div`
  font-size: 11px;
  font-weight: 400;
  color: ${C.placeholder};
  margin-top: 4px;
  text-align: center;
`;

export const TimelineComment = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${C.muted};
  margin-top: 4px;
  text-align: center;
`;

/* ============================================================
   Note sections (Accordion — Section 08)
   ============================================================ */
export const NoteSection = styled.div`
  border: 1px solid ${C.border};
  border-radius: ${C.radiusCard};
  margin-bottom: 8px;
  overflow: hidden;
  &:last-child {
    margin-bottom: 0;
  }
`;

export const NoteSectionHead = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  background: ${props => (props.open ? C.brandBlueTint : C.surface)};
  color: ${props => (props.open ? '#1A7DC4' : C.ink)};
  width: 100%;
  border: none;
  text-align: left;
  font-family: inherit;
  & .chev {
    color: ${props => (props.open ? '#1A7DC4' : C.slate)};
    transition: transform 0.18s;
    transform: rotate(${props => (props.open ? '90deg' : '0deg')});
    flex: none;
  }
`;

export const NoteSectionTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  flex: 1;
  color: inherit;
`;

export const SectionTag = styled.span`
  font-size: 12px;
  font-weight: 500;
  height: 22px;
  display: inline-flex;
  align-items: center;
  padding: 0 10px;
  border-radius: ${C.radiusPill};
  flex: none;
  background: ${props => props.suppressed ? '#FFF7ED' : C.brandBlueTint};
  color: ${props => props.suppressed ? '#C2410C' : '#1A7DC4'};
  border: 1px solid ${props => props.suppressed ? '#FCD34D' : '#C3DEFA'};
`;

export const NoteSectionBody = styled.div`
  padding: 12px 16px 16px 44px;
  background: ${C.white};
  font-size: 13px;
  font-weight: 400;
  color: ${C.ink};
  & p {
    margin: 8px 0;
  }
  & .ql-editor {
    padding: 0;
  }
`;

export const SuppressLine = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 12px;
  color: #c2410c;
  background: #fff7ed;
  border: 1px solid #fcd34d;
  border-radius: ${C.radiusBtn};
  padding: 8px 12px;
  margin: 4px 0 12px;
`;

/* ============================================================
   AI runs
   ============================================================ */
export const AiRun = styled.div`
  border: 1px solid ${C.border};
  border-radius: ${C.radiusCard};
  padding: 16px;
  margin-bottom: 16px;
  &:last-child {
    margin-bottom: 0;
  }
`;

export const AiRunHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
  & .t {
    font-size: 14px;
    font-weight: 600;
    color: ${C.ink};
  }
  & .m {
    font-size: 12px;
    font-weight: 400;
    color: ${C.placeholder};
  }
`;

/* Collapsible AI-activity card (matches the attachment card look). */
export const AiCard = styled.div`
  border: 1px solid ${C.border};
  border-radius: ${C.radiusBtn};
  margin-bottom: 8px;
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    border-color: ${C.brandBlue};
    box-shadow: 0 1px 4px rgba(71, 164, 234, 0.15);
  }
  &:last-child {
    margin-bottom: 0;
  }
`;

export const AiCardHead = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${props => (props.open ? C.surface : C.white)};
  border: none;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  &:hover {
    background: ${C.surface};
  }
  & .ic {
    width: 36px;
    height: 36px;
    border-radius: ${C.radiusBtn};
    background: ${C.brandBlueTint};
    display: grid;
    place-items: center;
    color: ${C.brandBlue};
    flex: none;
  }
  & .info {
    min-width: 0;
    flex: 1;
  }
  & .nm {
    font-size: 14px;
    font-weight: 500;
    color: ${C.ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  & .mt {
    font-size: 12px;
    font-weight: 400;
    color: ${C.placeholder};
  }
  & .chev {
    color: ${C.slate};
    flex: none;
    transition: transform 0.18s;
    transform: rotate(${props => (props.open ? '90deg' : '0deg')});
  }
`;

export const AiCardBody = styled.div`
  padding: 4px 12px 14px;
  border-top: 1px solid ${C.border};
`;

export const FieldLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${C.slate};
  margin: 16px 0 8px;
`;

export const CodeBlock = styled.div`
  font-size: 13px;
  font-weight: 400;
  line-height: 1.6;
  color: ${C.ink};
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-radius: ${C.radiusBtn};
  padding: 12px 16px;
  white-space: pre-wrap;
  word-break: break-word;
  ${props => props.scroll && 'max-height:300px;overflow:auto;'}
`;

/* ============================================================
   Right rail cards (Section 08)
   ============================================================ */
export const RailCard = styled.div`
  background: ${C.white};
  border: 1px solid ${C.border};
  border-radius: ${C.radiusCard};
  padding: 20px;
  margin-bottom: 16px;
  &:last-child {
    margin-bottom: 0;
  }
`;

export const RailCardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  & svg,
  & .anticon {
    color: ${C.brandBlue};
  }
  & .t {
    font-size: 16px;
    font-weight: 600;
    color: ${C.ink};
  }
  & .pill {
    font-size: 12px;
    font-weight: 500;
    background: ${C.brandBlue};
    color: ${C.white};
    padding: 0 8px;
    border-radius: 10px;
    margin-left: auto;
  }
`;

export const Kv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 13px;
  border-bottom: 1px solid ${C.rowBorder};
  &:last-of-type {
    border-bottom: none;
  }
  & .k {
    font-weight: 400;
    color: ${C.muted};
  }
  & .v {
    font-weight: 600;
    color: ${C.ink};
    text-align: right;
    word-break: break-all;
  }
`;

export const RailButton = styled(Button)`
  &&& {
    width: 100%;
    justify-content: center;
    margin-top: 12px;
    height: 36px;
    font-size: 14px;
    font-weight: 400;
    border: 1px solid ${C.borderStrong};
    background: ${C.white};
    color: ${C.ink};
    border-radius: ${C.radiusBtn};
  }
  &&&:hover {
    background: ${C.surface};
    border-color: ${C.borderStrong};
    color: ${C.ink};
  }
`;

export const RailHint = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: ${C.placeholder};
  margin: 12px 0 0;
  line-height: 1.4;
`;

export const Attachment = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid ${C.border};
  border-radius: ${C.radiusBtn};
  transition: border-color 0.15s ease, background 0.15s ease,
    box-shadow 0.15s ease;
  &:hover {
    border-color: ${C.brandBlue};
    background: ${C.surface};
    box-shadow: 0 1px 4px rgba(71, 164, 234, 0.15);
  }
  & .ic {
    width: 36px;
    height: 36px;
    border-radius: ${C.radiusBtn};
    background: ${C.brandBlueTint};
    display: grid;
    place-items: center;
    color: ${C.brandBlue};
    flex: none;
  }
  & .nm {
    font-size: 14px;
    font-weight: 500;
    color: ${C.ink};
  }
  & .mt {
    font-size: 12px;
    font-weight: 400;
    color: ${C.placeholder};
  }
  & .sz {
    margin-left: auto;
    text-align: right;
    font-size: 12px;
    font-weight: 400;
    color: ${C.slate};
  }
`;

export const Immutable = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${C.brandBlueTint};
  border: 1px solid #c3defa;
  border-radius: ${C.radiusBtn};
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 400;
  color: #1a7dc4;
  margin-top: 8px;
  & .anticon {
    color: #1a7dc4;
  }
`;

/* Print-option dropdown — Form input spec (Section 06) */
export const PrintOptionSelect = styled.select`
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  color: ${C.ink};
  background: ${C.white};
  border: 1px solid ${C.border};
  border-radius: ${C.radiusBtn};
  padding: 0 12px;
  cursor: pointer;
  height: 36px;
  &:hover {
    border-color: #c0cbd8;
  }
  &:focus {
    outline: none;
    border: 1.5px solid ${C.brandBlue};
    box-shadow: 0 0 0 3px rgba(71, 164, 234, 0.15);
  }
`;

/* Close button — Icon Neutral (Section 05), 36×36, returns to the grid */
export const DetailCloseButton = styled.button`
  flex: none;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  background: ${C.white};
  color: ${C.slate};
  border: 1px solid ${C.borderStrong};
  border-radius: ${C.radiusBtn};
  cursor: pointer;
  font-size: 15px;
  &:hover {
    background: ${C.surface};
    color: ${C.ink};
  }
`;

/* Skeleton loading block (shimmer). */
const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

export const SkeletonBlock = styled.div`
  height: ${props => props.height || '14px'};
  width: ${props => props.width || '100%'};
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    ${C.surface} 25%,
    ${C.border} 37%,
    ${C.surface} 63%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.3s ease-in-out infinite;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* hidden print target wrapper (react-to-print clones it, so display:none is fine) */
export const PrintHidden = styled.div`
  display: none;
`;
